// ─── Enums ────────────────────────────────────────────────────────────────────

export enum Role {
  SuperAdmin = 'SuperAdmin',
  Admin      = 'Admin',
  Manager    = 'Manager',
  Trainer    = 'Trainer',
  Student    = 'Student',
  Parent     = 'Parent',
}

export enum PaymentStatus {
  Paid    = 'paid',
  Pending = 'pending',
  Overdue = 'overdue',
  Partial = 'partial',
  Refunded = 'refunded',
}

export enum AttendanceStatus {
  Present = 'present',
  Absent  = 'absent',
  Late    = 'late',
  Excused = 'excused',
}

export enum AttendanceMethod {
  Manual = 'manual',
  QR     = 'qr',
  NFC    = 'nfc',
}

export enum StudentStatus {
  Active   = 'active',
  Inactive = 'inactive',
  Pending  = 'pending',
  Frozen   = 'frozen',
}

export enum LessonStatus {
  Scheduled  = 'scheduled',
  InProgress = 'in_progress',
  Completed  = 'completed',
  Cancelled  = 'cancelled',
}

export enum MessageType {
  SMS   = 'sms',
  Email = 'email',
  Push  = 'push',
}

// ─── Core Models ──────────────────────────────────────────────────────────────

export interface Tenant {
  id:          string
  name:        string
  slug:        string
  logoUrl:     string | null
  phone:       string | null
  email:       string | null
  address:     string | null
  plan:        'starter' | 'pro' | 'enterprise'
  isActive:    boolean
  createdAt:   string
}

export interface Branch {
  id:       string
  tenantId: string
  name:     string
  address:  string | null
  phone:    string | null
  isActive: boolean
}

export interface User {
  id:              string
  tenantId:        string
  firstName:       string
  lastName:        string
  fullName:        string
  email:           string
  phoneNumber:     string | null
  role:            Role
  profilePhotoUrl: string | null
  isActive:        boolean
  lastLoginAt:     string | null
  createdAt:       string
  certificateType: string | null
}

export interface Student {
  id:               string
  tenantId:         string
  branchId:         string
  branchName?:      string
  firstName:        string
  lastName:         string
  fullName?:        string
  birthDate:        string | null
  age?:             number
  gender:           number | 'male' | 'female' | 'other'
  photoUrl?:        string | null
  avatarUrl?:       string | null
  phone:            string | null
  email:            string | null
  address:          string | null
  bloodType?:       string | null
  medicalNotes?:    string | null
  notes?:           string | null
  emergencyContact?: string | null
  emergencyPhone?:  string | null
  parentName?:      string | null
  parentPhone?:     string | null
  parentEmail?:     string | null
  registrationDate?: string
  registeredAt?:    string
  isActive:         boolean
  status?:          StudentStatus
  userId?:          string | null
  qrCode?:          string | null
  groups?:          Group[]
  createdAt:        string
}

export interface Group {
  id:           string
  tenantId:     string
  branchId:     string
  branchName?:  string
  name:         string
  description?: string | null
  trainerId:    string
  trainerName?: string
  trainer?:     User | null
  coachId?:     string | null
  coach?:       User | null
  maxCapacity:  number
  capacity?:    number
  currentCount: number
  studentCount?: number
  ageMin:       number | null
  ageMax:       number | null
  isActive:     boolean
  createdAt:    string
  students?:    Student[]
}

export interface Lesson {
  id:          string
  groupId:     string
  group?:      Group | null
  groupName?:  string | null
  branchId?:   string
  branchName?: string | null
  title:       string | null
  startTime:   string
  endTime:     string
  location?:   string | null
  status?:     LessonStatus | null
  isRecurring?: boolean
  attendances?: Attendance[]
  createdAt:   string
}

export interface Attendance {
  id:             string
  lessonId:       string
  studentId:      string
  studentName:    string | null
  student?:       Pick<Student, 'id' | 'firstName' | 'lastName'> & { avatarUrl?: string | null; photoUrl?: string | null }
  status?:        AttendanceStatus
  method:         AttendanceMethod | string
  attendedAt:     string
  checkedInAt?:   string | null
  note:           string | null
  createdAt?:     string
}

export interface Payment {
  id:          string
  tenantId:    string
  studentId:   string
  studentName: string | null
  student?:    Pick<Student, 'id' | 'firstName' | 'lastName'>
  amount:      number
  currency:    string
  status:      PaymentStatus
  dueDate:     string
  paidAt:      string | null
  description: string | null
  invoiceUrl:  string | null
  createdAt:   string
}

export interface Expense {
  id:          string
  tenantId:    string
  branchId:    string | null
  category:    string
  description: string
  amount:      number
  currency:    string
  date:        string
  createdAt:   string
}

export interface Message {
  id:         string
  tenantId:   string
  senderId:   string
  type:       MessageType
  recipients: string[]
  subject:    string | null
  body:       string
  sentAt:     string | null
  status:     'draft' | 'queued' | 'sent' | 'failed'
  createdAt:  string
}

export interface Notification {
  id:        string
  userId:    string
  title:     string
  body:      string
  type:      'info' | 'success' | 'warning' | 'error'
  isRead:    boolean
  link:      string | null
  createdAt: string
}

// ─── Dashboard / Analytics ────────────────────────────────────────────────────

export interface DashboardStats {
  totalStudents:      number
  activeStudents:     number
  todayAttendance:    number
  todayAttendanceRate: number
  monthlyRevenue:     number
  pendingPayments:    number
  pendingPaymentsCount: number
  upcomingLessons:    number
}

export interface MonthlyRevenueData {
  month:   string
  gelir:   number
  gider:   number
}

export interface AttendanceTrend {
  date:        string
  present:     number
  absent:      number
  late:        number
}

// ─── API Response Wrappers ────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data:    T
  message: string | null
  success: boolean
}

export interface PaginatedResponse<T> {
  items:          T[]
  pageNumber:     number
  totalPages:     number
  totalCount:     number
  count:          number
  hasPreviousPage: boolean
  hasNextPage:    boolean
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface LoginCredentials {
  email:    string
  password: string
}

export interface AuthTokens {
  accessToken:  string
  refreshToken: string
  expiresAt:    string
}

export interface LoginResponse extends AuthTokens {
  user: User
}

// ─── Form Filters ─────────────────────────────────────────────────────────────

export interface StudentFilters {
  search?:   string
  status?:   StudentStatus
  groupId?:  string
  branchId?: string
  page?:     number
  pageSize?: number
  ordering?: string
}

export interface PaymentFilters {
  search?:    string
  status?:    PaymentStatus
  studentId?: string
  dateFrom?:  string
  dateTo?:    string
  page?:      number
  pageSize?:  number
}

export interface LessonFilters {
  groupId?:  string
  status?:   LessonStatus
  dateFrom?: string
  dateTo?:   string
  page?:     number
  pageSize?: number
}

// ─── UI State Types ───────────────────────────────────────────────────────────

export interface ToastMessage {
  id:      string
  type:    'success' | 'error' | 'warning' | 'info'
  title:   string
  description?: string
  duration?: number
}

export interface BreadcrumbItem {
  label: string
  href?: string
}

export type SortDirection = 'asc' | 'desc'

export interface SortConfig {
  key:       string
  direction: SortDirection
}

export interface TableColumn<T = unknown> {
  key:       string
  label:     string
  sortable?: boolean
  render?:   (value: unknown, row: T) => React.ReactNode
  className?: string
}
